import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client.js';
import { attachmentApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';
import { useSession } from '../auth/session.js';
import { Alert, Button, Spinner } from './ui.js';

function readableSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentListProps {
  readonly ticketId: string;
  readonly canAttach: boolean;
}

export function AttachmentList({ ticketId, canAttach }: AttachmentListProps): ReactNode {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const picker = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.tickets.attachments(ticketId),
    queryFn: () => attachmentApi.list(ticketId),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.tickets.attachments(ticketId) });
  };

  const upload = useMutation({
    mutationFn: (file: File) => attachmentApi.upload(ticketId, file),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'The file could not be attached.');
    },
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) => attachmentApi.remove(attachmentId),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setProblem(error instanceof ApiError ? error.message : 'The file could not be removed.');
    },
  });

  const items = data?.items ?? [];

  return (
    <section className="mt-7">
      <h3 className="eyebrow mb-2 text-muted">Files</h3>

      {problem === null ? null : <Alert tone="error">{problem}</Alert>}

      {isLoading ? (
        <div className="py-3 text-muted">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="mb-3 text-[13.5px] text-muted">Nothing attached yet.</p>
      ) : (
        <ul className="mb-3 flex flex-col gap-2">
          {items.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center gap-3 rounded-panel border border-hairline px-3 py-2.5 dark:border-dark-line"
            >
              <a
                href={attachmentApi.contentUrl(entry.id)}
                download={entry.fileName}
                className="min-w-0 flex-1"
              >
                <span className="block truncate text-[13.5px] font-semibold text-royal hover:underline">
                  {entry.fileName}
                </span>
                <span className="text-[11.5px] text-muted">
                  {readableSize(entry.byteSize)} · {entry.uploadedByName}
                </span>
              </a>

              {entry.uploadedById === user?.id ? (
                <button
                  type="button"
                  onClick={() => {
                    remove.mutate(entry.id);
                  }}
                  disabled={remove.isPending}
                  className="shrink-0 text-[12.5px] font-semibold text-critical hover:underline"
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canAttach ? (
        <>
          <input
            ref={picker}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) upload.mutate(file);
              event.target.value = '';
            }}
          />

          <Button
            size="sm"
            variant="secondary"
            loading={upload.isPending}
            onClick={() => {
              picker.current?.click();
            }}
          >
            Attach a file
          </Button>
        </>
      ) : null}
    </section>
  );
}
